/* =====================================================================================
   Docket — patent families, national filings and the deadlines that kill them.

   The shape matters:

     family       one invention, one priority date, one Paris year
     application  a filing in a jurisdiction, hanging off the family
     deadline     a dated obligation against either

   Missing a renewal annuity or the Paris twelve-month deadline is not recoverable. Neither
   generates a reminder from anyone: the attorney bills for what you instruct, the patent
   office simply lets it lapse. So this module exists to make silence impossible, and every
   deadline carries who is responsible — us, the attorney, or an annuity service — because
   "someone was handling it" is how a family is lost.

   `paris_deadline` is computed by the database from the priority date. It is never entered
   by hand: a mistyped date on the one deadline that cannot be extended is the single most
   expensive typo available in this product.
   ===================================================================================== */

import { BACKEND } from './db';
import { TENANT } from '@tenant';
import { explain } from './dberrors';

export const FAMILY_STATUS = ['draft', 'filed', 'active', 'granted_partial',
  'granted', 'lapsed', 'abandoned', 'sold'] as const;
export const STRATEGIC_VALUE = ['core', 'important', 'defensive', 'review', 'divest'] as const;
export const FTO_STATUS = ['cleared', 'favourable', 'concerns', 'blocked', 'not_assessed'] as const;

export const DEADLINE_TYPES = [
  'priority_year', 'pct_filing', 'national_phase', 'examination_request',
  'office_action_response', 'renewal_annuity', 'opposition', 'appeal',
  'annuity_surcharge', 'translation', 'assignment_recordal', 'other'] as const;
export const DEADLINE_STATUS = ['open', 'instructed', 'completed', 'missed',
  'waived', 'not_applicable'] as const;
export const RESPONSIBLE = ['attorney', 'us', 'annuity_service'] as const;

export interface Family {
  id: string; family_ref: string; title: string; pillar: string | null;
  short_description: string | null; proprietor: string | null;
  priority_date: string | null; priority_application: string | null;
  paris_deadline: string | null;
  status: typeof FAMILY_STATUS[number];
  strategic_value: typeof STRATEGIC_VALUE[number] | null;
  fto_status: typeof FTO_STATUS[number] | null; fto_notes: string | null;
  attorney_firm: string | null; attorney_ref: string | null; notes: string | null;
  applications?: Filing[];
  open_deadlines?: number;
}

export interface Filing {
  id: string; family_id: string; jurisdiction: string;
  application_type: string | null; application_number: string | null;
  publication_number: string | null; patent_number: string | null;
  filed_on: string | null; granted_on: string | null; expires_on: string | null;
  status: string | null; agent_ref: string | null;
  annual_cost: number | null; cost_currency: string | null;
}

export interface Deadline {
  id: string; family_id: string | null; application_id: string | null;
  family_ref?: string; jurisdiction?: string;
  deadline_type: typeof DEADLINE_TYPES[number];
  description: string | null; due_date: string;
  extendable_to: string | null; fee_amount: number | null; fee_currency: string | null;
  responsible: typeof RESPONSIBLE[number];
  status: typeof DEADLINE_STATUS[number];
  completed_on: string | null; critical: boolean; recurrence: string | null;
}

const live = () => BACKEND === 'supabase';

async function orgId(): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  if (!data?.id) throw new Error('No organisation visible to this member.');
  return data.id;
}

export async function fetchFamilies(): Promise<Family[]> {
  if (!live()) return (TENANT.seed.family ?? []) as never;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('docket').from('families')
    .select('*, applications(*)')
    .order('family_ref');
  if (error) throw new Error(explain(error.message, 'save this docket record'));
  return data ?? [];
}

export async function fetchDeadlines(): Promise<Deadline[]> {
  if (!live()) return (TENANT.seed.deadline ?? []) as never;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('docket').from('deadlines')
    .select('*, family:family_id(family_ref), application:application_id(jurisdiction)')
    .order('due_date', { ascending: true });
  if (error) throw new Error(explain(error.message, 'save this docket record'));
  return (data ?? []).map((d: any) => ({
    ...d,
    family_ref: d.family?.family_ref ?? null,
    jurisdiction: d.application?.jurisdiction ?? null,
  }));
}

export async function createFamily(f: {
  familyRef: string; title: string; pillar?: string; priorityDate?: string;
  priorityApplication?: string; proprietor?: string; attorneyFirm?: string;
  strategicValue?: string; shortDescription?: string;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('docket').from('families').insert({
    org_id: await orgId(),
    family_ref: f.familyRef, title: f.title, pillar: f.pillar || null,
    // paris_deadline is generated from this by the database. Never sent from here.
    priority_date: f.priorityDate || null,
    priority_application: f.priorityApplication || null,
    proprietor: f.proprietor || null, attorney_firm: f.attorneyFirm || null,
    strategic_value: f.strategicValue || 'important',
    short_description: f.shortDescription || null,
    status: 'draft',
  });
  if (error) {
    if (error.code === '23505') throw new Error(`Family reference ${f.familyRef} already exists.`);
    throw new Error(explain(error.message, 'save this docket record'));
  }
}

export async function updateFamily(id: string, patch: Record<string, unknown>) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('docket').from('families').update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'save this docket record'));
}

export async function createFiling(f: {
  familyId: string; jurisdiction: string; applicationType?: string;
  applicationNumber?: string; filedOn?: string; agentRef?: string;
  annualCost?: number; costCurrency?: string;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('docket').from('applications').insert({
    org_id: await orgId(), family_id: f.familyId,
    jurisdiction: f.jurisdiction.toUpperCase(),
    application_type: f.applicationType || null,
    application_number: f.applicationNumber || null,
    filed_on: f.filedOn || null, agent_ref: f.agentRef || null,
    annual_cost: f.annualCost ?? null, cost_currency: f.costCurrency || 'GBP',
    status: 'pending',
  });
  if (error) throw new Error(explain(error.message, 'save this docket record'));
}

/**
 * Set an action against a family or a filing. `responsible` is required rather than
 * defaulted, because an unassigned deadline is the failure mode this module exists to
 * prevent: everyone assumes the attorney has it, and the attorney is waiting on instruction.
 */
export async function createDeadline(d: {
  familyId?: string | null; applicationId?: string | null;
  deadlineType: string; description?: string; dueDate: string;
  responsible: typeof RESPONSIBLE[number];
  critical?: boolean; feeAmount?: number; feeCurrency?: string;
  extendableTo?: string; recurrence?: string;
}) {
  if (!d.familyId && !d.applicationId) {
    throw new Error('An action must hang off a family or a filing.');
  }
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('docket').from('deadlines').insert({
    org_id: await orgId(),
    family_id: d.familyId || null, application_id: d.applicationId || null,
    deadline_type: d.deadlineType, description: d.description || null,
    due_date: d.dueDate, extendable_to: d.extendableTo || null,
    fee_amount: d.feeAmount ?? null, fee_currency: d.feeCurrency || 'GBP',
    responsible: d.responsible, status: 'open',
    critical: d.critical ?? false, recurrence: d.recurrence || 'none',
  });
  if (error) throw new Error(explain(error.message, 'save this docket record'));
}

export async function updateDeadline(id: string, patch: Record<string, unknown>) {
  const { supabase } = await import('./supabase');
  // Completing without a date leaves a record that says "done" and cannot say when, which is
  // useless to an attorney reconstructing a prosecution history.
  if (patch.status === 'completed' && !patch.completed_on) {
    patch.completed_on = new Date().toISOString().slice(0, 10);
  }
  const { error } = await supabase().schema('docket').from('deadlines').update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'save this docket record'));
}

/**
 * Remove an action. Only ever for something entered in error: a completed or missed deadline
 * is part of the prosecution history and gets its status changed rather than deleted. An
 * attorney reconstructing what happened needs the misses more than the hits.
 */
export async function deleteDeadline(id: string, status: string) {
  if (status === 'completed' || status === 'missed') {
    throw new Error(
      'A completed or missed action cannot be deleted; it is part of the prosecution history. ' +
      'Change its status to "waived" or "not applicable" instead.',
    );
  }
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('docket').from('deadlines').delete().eq('id', id);
  if (error) throw new Error(explain(error.message, 'save this docket record'));
}

export function daysTo(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso + 'T00:00:00').getTime() - Date.now()) / 86_400_000);
}
